import * as React from 'react'
import { useDecideReply, useDecideReview, useReviewsToApprove, useSettleReport } from '@/hooks/useVendorReviews'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { requestErrorMessage } from '@/helpers/apiClient'
import { formatEventDate } from '@/lib/eventTime'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { decideVendorReviewInputSchema } from '@lib/input/vendor-review.input'
import { REVIEW_DECLINE_REASON_MAX } from '@lib/constants/vendor-reviews'
import { reviewerLine } from '@lib/helpers/vendor-reviews'
import { Stars } from '@/components/reviews/ShownReviews'
import type { ReplyToApproveDto, ReportToSettleDto, ReviewAnsweredDto, ReviewToApproveDto } from '@lib/dto/vendor-review.dto'

const SENT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/** A review or a company's reply, as the admin read it when they chose to decline it. */
interface Declining {
  kind: 'review' | 'reply'
  /** The review's id, for both: a reply is the review's. */
  reviewId: string
  /** Whose: the company the review is of. */
  vendorName: string
  sentAt: string
}

interface DeclineDialogProps {
  declining: Declining
  /** When the same one, as the list has it now, was sent; undefined once it's gone (decided). */
  currentSentAt: string | undefined
  onClose: () => void
}

/**
 * Declining needs a reason: the review's writer reads it on their event page.
 * It declines the version the admin read; if its writer sends it again while
 * the dialog is open, the dialog says so rather than failing on every try.
 */
function DeclineDialog({ declining, currentSentAt, onClose }: DeclineDialogProps) {
  const decideReview = useDecideReview()
  const decideReply = useDecideReply()
  const decide = declining.kind === 'review' ? decideReview : decideReply
  const [reason, setReason] = React.useState('')
  const [error, setError] = React.useState<string | undefined>(undefined)
  const changed = currentSentAt !== undefined && currentSentAt !== declining.sentAt
  const what = declining.kind === 'review' ? 'review' : 'reply'

  // Decided by another admin meanwhile: nothing left to decline.
  React.useEffect(() => {
    if (currentSentAt === undefined) onClose()
  }, [currentSentAt, onClose])

  function submit(e: React.FormEvent): void {
    e.preventDefault()
    if (changed) return
    const parsed = decideVendorReviewInputSchema.safeParse({ decision: 'decline', sentAt: declining.sentAt, reason })
    if (!parsed.success) {
      setError(parsed.error.issues.find((i) => i.path[0] === 'reason')?.message)
      return
    }
    setError(undefined)
    decide.mutate({ reviewId: declining.reviewId, input: parsed.data }, { onSuccess: onClose })
  }

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Decline this {what} {declining.kind === 'review' ? 'of' : 'from'} {declining.vendorName}
          </DialogTitle>
          <DialogDescription>
            {declining.kind === 'review'
              ? 'It stays off their page. Its writer can change it and send it again.'
              : 'It stays off their page. They can change it and send it again.'}
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit} noValidate>
          <Field label={declining.kind === 'review' ? 'Why? The writer sees this.' : 'Why? The company sees this.'} htmlFor="decline-reason" error={error} hint={`${String(reason.length)}/${String(REVIEW_DECLINE_REASON_MAX)}`}>
            <Textarea id="decline-reason" rows={3} maxLength={REVIEW_DECLINE_REASON_MAX} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {changed ? (
            <FormError>
              {declining.kind === 'review' ? 'Its writer' : 'The company'} changed it since you opened this. Close this and read it again before deciding.
            </FormError>
          ) : null}
          {decide.isError && !changed ? <FormError>{requestErrorMessage(decide.error)}</FormError> : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={decide.isPending || changed}>
              {decide.isPending ? 'Declining…' : `Decline ${what}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

interface ReviewCardProps {
  review: ReviewToApproveDto
  onDecline: () => void
}

/**
 * One waiting review, with what only an admin sees: who sent it, the venue and
 * the date. A refused approval says why, and the list shows it as it is now.
 */
function ReviewCard({ review, onDecline }: ReviewCardProps) {
  const decide = useDecideReview()
  const writer = review.writtenBy === null ? 'Writer’s account is gone' : `Written by ${review.writtenBy.name} (${review.writtenBy.email})`
  return (
    <Card asChild className="space-y-2 p-4">
      <li>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-medium">{review.vendor.name}</span>
          <span className="text-xs text-ink-muted">Sent {SENT.format(new Date(review.sentAt))}</span>
        </div>
        <p className="m-0 text-sm">
          <Stars rating={review.rating} /> <span className="text-ink-muted">{reviewerLine(review.reviewer, review.month)}</span>
        </p>
        <p className="m-0 text-xs text-ink-muted">
          {writer} · {review.venueName ?? 'Event deleted'} · {formatEventDate(review.eventDate)}
        </p>
        <p className="m-0 whitespace-pre-wrap text-sm">{review.body}</p>
        {decide.isError ? <FormError>{requestErrorMessage(decide.error)}</FormError> : null}
        <div className="flex gap-2 pt-1">
          <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ reviewId: review.id, input: { decision: 'approve', sentAt: review.sentAt } })}>
            {decide.isPending ? 'Approving…' : 'Approve'}
          </Button>
          <Button size="sm" variant="outline" disabled={decide.isPending} onClick={onDecline}>
            Decline
          </Button>
        </div>
      </li>
    </Card>
  )
}

interface AnsweredProps {
  review: ReviewAnsweredDto
}

/** The published review a reply or report is about, read beside it. */
function Answered({ review }: AnsweredProps) {
  return (
    <div className="space-y-1 rounded-md bg-surface-sunken p-3">
      <p className="m-0 text-xs text-ink-muted">
        <Stars rating={review.rating} /> {reviewerLine(review.reviewer, review.month)}
      </p>
      <p className="m-0 whitespace-pre-wrap text-sm">{review.body}</p>
    </div>
  )
}

interface ReplyCardProps {
  reply: ReplyToApproveDto
  onDecline: () => void
}

/** A company's reply waiting, under the review it answers. */
function ReplyCard({ reply, onDecline }: ReplyCardProps) {
  const decide = useDecideReply()
  const writer = reply.writtenBy === null ? 'Writer’s account is gone' : `Written by ${reply.writtenBy.name} (${reply.writtenBy.email})`
  return (
    <Card asChild className="space-y-2 p-4">
      <li>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-medium">Reply from {reply.vendor.name}</span>
          <span className="text-xs text-ink-muted">Sent {SENT.format(new Date(reply.sentAt))}</span>
        </div>
        <Answered review={reply.review} />
        <p className="m-0 text-xs text-ink-muted">{writer}</p>
        <p className="m-0 whitespace-pre-wrap text-sm">{reply.body}</p>
        {decide.isError ? <FormError>{requestErrorMessage(decide.error)}</FormError> : null}
        <div className="flex gap-2 pt-1">
          <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ reviewId: reply.reviewId, input: { decision: 'approve', sentAt: reply.sentAt } })}>
            {decide.isPending ? 'Approving…' : 'Approve'}
          </Button>
          <Button size="sm" variant="outline" disabled={decide.isPending} onClick={onDecline}>
            Decline
          </Button>
        </div>
      </li>
    </Card>
  )
}

interface ReportCardProps {
  report: ReportToSettleDto
}

/** A company's report of a published review: keep it up, or take it down for good. */
function ReportCard({ report }: ReportCardProps) {
  const settle = useSettleReport()
  const [confirming, setConfirming] = React.useState(false)
  const reporter = report.reportedBy === null ? 'Reporter’s account is gone' : `Reported by ${report.reportedBy.name} (${report.reportedBy.email})`
  return (
    <Card asChild className="space-y-2 p-4">
      <li>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-medium">{report.vendor.name} reported a review</span>
          <span className="text-xs text-ink-muted">Reported {SENT.format(new Date(report.reportedAt))}</span>
        </div>
        <Answered review={report.review} />
        <p className="m-0 text-xs text-ink-muted">{reporter}</p>
        <p className="m-0 whitespace-pre-wrap text-sm">&ldquo;{report.reason}&rdquo;</p>
        {settle.isError && !confirming ? <FormError>{requestErrorMessage(settle.error)}</FormError> : null}
        <div className="flex gap-2 pt-1">
          <Button size="sm" variant="outline" disabled={settle.isPending} onClick={() => settle.mutate({ reviewId: report.reviewId, input: { decision: 'keep' } })}>
            Keep it
          </Button>
          <Button size="sm" variant="outline" disabled={settle.isPending} onClick={() => setConfirming(true)}>
            Take it down
          </Button>
        </div>
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Take this review down?"
          description="It comes off the vendor’s page and profile for good, with any reply to it. Its writer sees it was taken down."
          confirmLabel="Take it down"
          destructive
          busy={settle.isPending}
          error={settle.isError ? requestErrorMessage(settle.error) : null}
          onConfirm={() => settle.mutate({ reviewId: report.reviewId, input: { decision: 'remove' } }, { onSuccess: () => setConfirming(false) })}
        />
      </li>
    </Card>
  )
}

/**
 * Reviews waiting for FLOH Venues' admins (docs/vendor-growth.spec.md,
 * "Reviews"): nothing shows on a vendor's page until it's approved here. Then
 * vendors' replies, approved the same way, and vendors' reports of published
 * reviews, each kept up or taken down. Each kind the longest waiting first.
 * Approving publishes the version read; one changed since is refused and the
 * list shows it as it is now.
 */
export function ReviewsToApprovePage() {
  const waiting = useReviewsToApprove()
  const [declining, setDeclining] = React.useState<Declining | null>(null)
  const closeDecline = React.useCallback(() => setDeclining(null), [])
  const data = waiting.data
  // An older API doesn't say how many wait in all; each list is then all of them.
  const counts = {
    reviews: data ? Math.max(data.waiting, data.reviews.length) : 0,
    replies: data ? Math.max(data.repliesWaiting, data.replies.length) : 0,
    reports: data ? Math.max(data.reportsWaiting, data.reports.length) : 0,
  }
  const total = counts.reviews + counts.replies + counts.reports

  function currentSentAt(d: Declining): string | undefined {
    if (d.kind === 'review') return data?.reviews.find((r) => r.id === d.reviewId)?.sentAt
    return data?.replies.find((r) => r.reviewId === d.reviewId)?.sentAt
  }

  function section(title: string, shown: number, count: number, items: React.ReactNode) {
    if (count === 0) return null
    return (
      <section className="space-y-3">
        <h2 className="m-0 text-sm font-semibold">
          {title} ({String(count)})
        </h2>
        <ul className="m-0 list-none space-y-3 p-0">{items}</ul>
        {count > shown ? (
          <p className="m-0 text-sm text-ink-muted">Showing the {String(shown)} that have waited longest. The rest come up as these are decided.</p>
        ) : null}
      </section>
    )
  }

  function list() {
    if (waiting.isPending) return <LoadingState label="Loading reviews…" />
    if (waiting.isError) return <ErrorState message={requestErrorMessage(waiting.error)} onRetry={() => void waiting.refetch()} />
    if (total === 0) return <p className="m-0 text-sm text-ink-muted">Nothing is waiting.</p>
    const d = waiting.data
    return (
      <>
        {section(
          'Reviews',
          d.reviews.length,
          counts.reviews,
          d.reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              onDecline={() => setDeclining({ kind: 'review', reviewId: review.id, vendorName: review.vendor.name, sentAt: review.sentAt })}
            />
          )),
        )}
        {section(
          'Replies',
          d.replies.length,
          counts.replies,
          d.replies.map((reply) => (
            <ReplyCard
              key={reply.reviewId}
              reply={reply}
              onDecline={() => setDeclining({ kind: 'reply', reviewId: reply.reviewId, vendorName: reply.vendor.name, sentAt: reply.sentAt })}
            />
          )),
        )}
        {section(
          'Reports',
          d.reports.length,
          counts.reports,
          d.reports.map((report) => <ReportCard key={report.reviewId} report={report} />),
        )}
      </>
    )
  }

  return (
    <PageContainer className="space-y-6">
      <PageHeader
        title="Reviews to approve"
        description="Nothing shows on a vendor’s page until it’s approved here."
        meta={data ? `${String(total)} waiting` : undefined}
      />
      {list()}
      {declining === null ? null : <DeclineDialog declining={declining} currentSentAt={currentSentAt(declining)} onClose={closeDecline} />}
    </PageContainer>
  )
}
